import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import EmailMediaService from "@/Service/EmailMediaService";
import {
  ALIGN_OPTIONS,
  WIDTH_OPTIONS,
  formatBytes,
  generateImageHtml,
  isSafeLinkUrl,
} from "./emailHtml";
import {
  DropZone,
  EmailPreviewSurface,
  FieldLabel,
  MediaGrid,
  MediaTile,
  SettingsLayout,
} from "./elements";

const ACCEPT = "image/jpeg,image/png,image/gif,.jpg,.jpeg,.png,.gif";
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/gif"];
/** Above this the email gets slow to open on mobile data; warn, don't block. */
const LARGE_FILE_BYTES = 1024 * 1024;

const GIF_NOTE =
  "Animated GIFs work in many email clients, but some clients may display only the first frame. Make sure the first frame also communicates the message.";

const describeError = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.message) return data.message;
  if (error?.response?.status === 403) return "Not authorised. This page requires an admin account.";
  return fallback;
};

const capitalise = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
};

const altFromFilename = (name = "") =>
  name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim();

/**
 * Insert → Image / GIF. Upload or pick from the library, then size/align/link the image.
 * Two stages in one component: library (no selection) → settings (selection made).
 */
const ImageInsert = ({ onInsert }) => {
  const [assets, setAssets] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [maxSizeBytes, setMaxSizeBytes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const [selected, setSelected] = useState(null);
  const [widthPercent, setWidthPercent] = useState(100);
  const [maxWidthPx, setMaxWidthPx] = useState("");
  const [align, setAlign] = useState("center");
  const [alt, setAlt] = useState("");
  const [href, setHref] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const searchTimer = useRef(null);

  const load = useCallback(async (q) => {
    setLoading(true);
    setLoadError("");
    try {
      const data = await EmailMediaService.listEmailMedia(q);
      setAssets(data?.assets || []);
      setConfigured(data?.configured !== false);
      if (data?.maxSizeBytes) setMaxSizeBytes(data.maxSizeBytes);
    } catch (error) {
      console.error("Failed to load media library", error);
      setLoadError(describeError(error, "Could not load the media library."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load("");
  }, [load]);

  const handleQueryChange = (value) => {
    setQuery(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(value.trim()), 300);
  };

  const select = useCallback((asset) => {
    setSelected(asset);
    setAlt(altFromFilename(asset.originalFilename));
    setWidthPercent(100);
    setMaxWidthPx("");
    setAlign("center");
    setHref("");
    setConfirmRemove(false);
  }, []);

  const handleFiles = useCallback(
    async (fileList) => {
      const file = fileList && fileList[0];
      if (!file) return;
      setUploadError("");
      if (!ACCEPTED_TYPES.includes(file.type)) {
        setUploadError("Only JPEG, PNG and GIF images are accepted (no SVG).");
        return;
      }
      if (maxSizeBytes && file.size > maxSizeBytes) {
        setUploadError(`That file is ${formatBytes(file.size)}; the limit is ${formatBytes(maxSizeBytes)}.`);
        return;
      }
      setUploading(true);
      try {
        const asset = await EmailMediaService.uploadEmailMedia(file);
        setAssets((current) => [asset, ...current.filter((a) => a.id !== asset.id)]);
        select(asset);
      } catch (error) {
        console.error("Failed to upload email media", error);
        setUploadError(describeError(error, "Upload failed."));
      } finally {
        setUploading(false);
      }
    },
    [maxSizeBytes, select]
  );

  const onDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    handleFiles(event.dataTransfer?.files);
  };

  const handleRemove = async () => {
    if (!selected) return;
    setRemoving(true);
    try {
      await EmailMediaService.archiveEmailMedia(selected.id);
      setAssets((current) => current.filter((a) => a.id !== selected.id));
      setSelected(null);
      setConfirmRemove(false);
    } catch (error) {
      console.error("Failed to remove email media", error);
      setLoadError(describeError(error, "Could not remove the image from the library."));
    } finally {
      setRemoving(false);
    }
  };

  const linkGiven = href.trim().length > 0;
  const linkOk = !linkGiven || isSafeLinkUrl(href);
  const customPx = maxWidthPx === "" ? undefined : Number(maxWidthPx);
  const customPxOk = customPx === undefined || (Number.isInteger(customPx) && customPx >= 40 && customPx <= 2000);

  const html = useMemo(
    () =>
      selected
        ? generateImageHtml({
            src: selected.publicUrl,
            alt,
            widthPercent,
            maxWidthPx: customPxOk ? customPx : undefined,
            align,
            href: linkOk && linkGiven ? href : undefined,
          })
        : "",
    [selected, alt, widthPercent, customPx, customPxOk, align, href, linkOk, linkGiven]
  );

  const isGif = selected?.mimeType === "image/gif";

  // ---- stage 2: settings -----------------------------------------------------------
  if (selected) {
    return (
      <Stack spacing={2.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="body2" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            <strong>{selected.originalFilename}</strong>{" "}
            <span style={{ opacity: 0.7 }}>
              {selected.width}×{selected.height} · {formatBytes(selected.sizeBytes)}
            </span>
          </Typography>
          <Button size="small" onClick={() => setSelected(null)} data-testid="image-insert-back">
            Choose another
          </Button>
        </Stack>

        {isGif && <Alert severity="info" variant="outlined">{GIF_NOTE}</Alert>}
        {selected.sizeBytes > LARGE_FILE_BYTES && (
          <Alert severity="warning" variant="outlined">
            This file is {formatBytes(selected.sizeBytes)}. Large images load slowly on mobile
            and some clients clip heavy emails — consider a smaller export.
          </Alert>
        )}

        <SettingsLayout>
          <Stack spacing={2}>
            <div>
              <FieldLabel>Width</FieldLabel>
              <ToggleButtonGroup
                value={widthPercent}
                exclusive
                size="small"
                fullWidth
                onChange={(_, v) => v && setWidthPercent(v)}
              >
                {WIDTH_OPTIONS.map((w) => (
                  <ToggleButton key={w} value={w} data-testid={`image-width-${w}`}>
                    {w}%
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <TextField
                label="Custom max width (px, optional)"
                value={maxWidthPx}
                onChange={(e) => setMaxWidthPx(e.target.value.replace(/[^\d]/g, ""))}
                size="small"
                fullWidth
                sx={{ mt: 1.5 }}
                error={!customPxOk}
                helperText={customPxOk ? " " : "Between 40 and 2000"}
                inputProps={{ inputMode: "numeric", "data-testid": "image-max-width" }}
              />
            </div>

            <div>
              <FieldLabel>Alignment</FieldLabel>
              <ToggleButtonGroup
                value={align}
                exclusive
                size="small"
                fullWidth
                onChange={(_, v) => v && setAlign(v)}
              >
                {ALIGN_OPTIONS.map((a) => (
                  <ToggleButton key={a} value={a} data-testid={`image-align-${a}`}>
                    {capitalise(a)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </div>

            <TextField
              label="Alt text"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder="Clippn Dashboard mobile upload"
              fullWidth
              size="small"
              inputProps={{ maxLength: 200, "data-testid": "image-alt" }}
            />
            <TextField
              label="Link (optional)"
              value={href}
              onChange={(e) => setHref(e.target.value)}
              placeholder="https://apps.apple.com/…"
              fullWidth
              size="small"
              error={!linkOk}
              helperText={linkOk ? " " : "Must start with https:// or http://"}
              inputProps={{ inputMode: "url", "data-testid": "image-link" }}
            />
          </Stack>

          <div>
            <FieldLabel>Preview</FieldLabel>
            {/* Safe: generated from escaped values, never raw user HTML. */}
            <EmailPreviewSurface dangerouslySetInnerHTML={{ __html: html }} />
          </div>
        </SettingsLayout>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1}
        >
          {confirmRemove ? (
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: "warning.main" }}>
                This image may already be used by previously sent emails. It will only be
                hidden from the library — the file itself stays online so those emails keep
                working.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" color="warning" variant="outlined" onClick={handleRemove} disabled={removing}>
                  Remove from library
                </Button>
                <Button size="small" onClick={() => setConfirmRemove(false)}>Keep</Button>
              </Stack>
            </Stack>
          ) : (
            <Button size="small" color="inherit" sx={{ opacity: 0.7 }} onClick={() => setConfirmRemove(true)}>
              Remove from library…
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => onInsert(html)}
            disabled={!linkOk || !customPxOk}
            data-testid="image-insert-submit"
          >
            Insert into email
          </Button>
        </Stack>
      </Stack>
    );
  }

  // ---- stage 1: upload + library ---------------------------------------------------
  return (
    <Stack spacing={2}>
      {!configured && (
        <Alert severity="warning" variant="outlined">
          Media uploads are not configured on this environment yet (EMAIL_MEDIA_BUCKET /
          EMAIL_MEDIA_PUBLIC_BASE_URL). Existing library items can still be inserted.
        </Alert>
      )}

      <DropZone
        $active={dragActive}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        data-testid="media-dropzone"
      >
        <input
          type="file"
          accept={ACCEPT}
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
          disabled={uploading || !configured}
          data-testid="media-file-input"
        />
        {uploading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <span>Uploading…</span>
          </Stack>
        ) : (
          <>
            <strong>Upload new</strong>
            <span>
              Drag &amp; drop or tap to choose · JPEG, PNG, GIF
              {maxSizeBytes ? ` · up to ${formatBytes(maxSizeBytes)}` : ""}
            </span>
          </>
        )}
      </DropZone>
      {uploadError && <Alert severity="error" variant="outlined">{uploadError}</Alert>}

      <TextField
        label="Search media"
        value={query}
        onChange={(e) => handleQueryChange(e.target.value)}
        size="small"
        fullWidth
        inputProps={{ "data-testid": "media-search" }}
      />

      {loadError && <Alert severity="error" variant="outlined">{loadError}</Alert>}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : assets.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 3 }}>
          {query ? "No media matches that name." : "No media yet — upload your first image or GIF above."}
        </Typography>
      ) : (
        <MediaGrid data-testid="media-grid">
          {assets.map((asset) => (
            <MediaTile
              key={asset.id}
              type="button"
              onClick={() => select(asset)}
              title={asset.originalFilename}
              data-testid={`media-asset-${asset.id}`}
            >
              <img className="thumb" src={asset.publicUrl} alt={asset.originalFilename} loading="lazy" />
              <div className="meta">
                <div className="name">{asset.originalFilename}</div>
                <div>
                  {asset.width}×{asset.height} · {formatBytes(asset.sizeBytes)} ·{" "}
                  {(asset.mimeType || "").replace("image/", "").toUpperCase()}
                </div>
                <div>{formatDate(asset.createdAt)}</div>
              </div>
            </MediaTile>
          ))}
        </MediaGrid>
      )}
    </Stack>
  );
};

export default ImageInsert;
